/**
 * Phase 110 Plan 110-08 (Surface A) — TanStack Query hooks for
 * ProjectDiscountConfig / ProjectDiscountTier.
 *
 * Per-project query keys (`['projectDiscount', projectId]` and
 * `['projectDiscountTiers', projectId]`) so switching projects never
 * poisons the cache, and a mutation only invalidates its own resource.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjectDiscountConfig,
  upsertProjectDiscountConfig,
  fetchProjectDiscountTiers,
  createProjectDiscountTier,
  updateProjectDiscountTier,
  deleteProjectDiscountTier,
  type UpsertProjectDiscountConfigInput,
  type CreateProjectDiscountTierInput,
  type UpdateProjectDiscountTierInput,
} from '../services/api/projectDiscount';

export function useProjectDiscountConfig(projectId: number | undefined) {
  return useQuery({
    queryKey: ['projectDiscount', projectId],
    queryFn: () => fetchProjectDiscountConfig(projectId!),
    enabled: !!projectId,
  });
}

export function useProjectDiscountTiers(projectId: number | undefined) {
  return useQuery({
    queryKey: ['projectDiscountTiers', projectId],
    queryFn: () => fetchProjectDiscountTiers(projectId!),
    enabled: !!projectId,
  });
}

export function useUpsertProjectDiscountConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: UpsertProjectDiscountConfigInput;
    }) => upsertProjectDiscountConfig(projectId, input),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectDiscount', projectId] }),
  });
}

export function useCreateProjectDiscountTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: CreateProjectDiscountTierInput;
    }) => createProjectDiscountTier(projectId, input),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectDiscountTiers', projectId] }),
  });
}

export function useUpdateProjectDiscountTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      tierId,
      input,
    }: {
      projectId: number;
      tierId: number;
      input: UpdateProjectDiscountTierInput;
    }) => updateProjectDiscountTier(projectId, tierId, input),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectDiscountTiers', projectId] }),
  });
}

export function useDeleteProjectDiscountTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, tierId }: { projectId: number; tierId: number }) =>
      deleteProjectDiscountTier(projectId, tierId),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectDiscountTiers', projectId] }),
  });
}
